import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useDepenses } from "../DepensesProvider";
import { DEPENSE_CATEGORIE_OPTIONS, type Depense, type DepenseCategorie, type DepenseInput } from "../types";

interface DepenseFormProps {
  open: boolean;
  onClose: () => void;
  defaultImmeubleId?: string;
  defaultBienId?: string;
  initial?: Depense;
}

interface FormState {
  libelle: string;
  categorie: DepenseCategorie;
  montant: string;
  date: string;
  immeubleId: string;
  bienId: string;
  description: string;
  justificatif: string;
}

const EMPTY_STATE: FormState = {
  libelle: "",
  categorie: "reparation",
  montant: "",
  date: "",
  immeubleId: "",
  bienId: "",
  description: "",
  justificatif: "",
};

export default function DepenseForm({ open, onClose, defaultImmeubleId, defaultBienId, initial }: DepenseFormProps) {
  const { immeubles } = useImmeubles();
  const { biens, getBien } = useBiens();
  const { addDepense, updateDepense } = useDepenses();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        libelle: initial.libelle,
        categorie: initial.categorie,
        montant: String(initial.montant),
        date: initial.date,
        immeubleId: initial.immeubleId,
        bienId: initial.bienId,
        description: initial.description,
        justificatif: initial.justificatif,
      });
      setErrors({});
      return;
    }
    setForm(() => {
      const base: FormState = { ...EMPTY_STATE, date: new Date().toISOString().slice(0, 10) };
      if (defaultImmeubleId) base.immeubleId = defaultImmeubleId;
      if (defaultBienId) {
        base.bienId = defaultBienId;
        const bien = getBien(defaultBienId);
        if (bien) base.immeubleId = bien.immeubleId;
      }
      return base;
    });
    setErrors({});
  }, [open, initial, defaultImmeubleId, defaultBienId, getBien]);

  const immeubleOptions = useMemo(
    () => immeubles.map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      if (key === "immeubleId") next.bienId = "";
      return next;
    });
  };

  const handleJustificatif = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    update("justificatif", file ? file.name : "");
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.libelle.trim()) nextErrors.libelle = "Indiquez un libellé.";
    if (!form.montant.trim() || Number(form.montant) <= 0) nextErrors.montant = "Indiquez un montant valide.";
    if (!form.date) nextErrors.date = "Indiquez la date de la dépense.";
    if (!form.immeubleId && !form.bienId) nextErrors.cible = "Rattachez la dépense à un immeuble ou à un bien.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: DepenseInput = {
      libelle: form.libelle.trim(),
      categorie: form.categorie,
      montant: Math.max(0, Number(form.montant) || 0),
      date: form.date,
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      description: form.description.trim(),
      justificatif: form.justificatif,
    };

    if (initial) {
      updateDepense(initial.id, input);
    } else {
      addDepense(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier la dépense" : "Ajouter une dépense"}
      description="Enregistrez une charge liée à un immeuble ou un bien. Libellé, catégorie, montant, date et rattachement sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="depense-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer la dépense
          </button>
        </>
      }
    >
      <form id="depense-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Libellé" htmlFor="dep-libelle" required error={errors.libelle} className="sm:col-span-2">
            <Input
              id="dep-libelle"
              value={form.libelle}
              onChange={(event) => update("libelle", event.target.value)}
              placeholder="Ex. Facture électricité janvier"
              maxLength={120}
              invalid={Boolean(errors.libelle)}
            />
          </Field>

          <Field label="Catégorie" htmlFor="dep-categorie" required>
            <Select
              id="dep-categorie"
              value={form.categorie}
              onChange={(event) => update("categorie", event.target.value)}
              options={DEPENSE_CATEGORIE_OPTIONS}
            />
          </Field>

          <Field label="Montant (FCFA)" htmlFor="dep-montant" required error={errors.montant}>
            <Input
              id="dep-montant"
              type="number"
              min={0}
              max={100000000}
              value={form.montant}
              onChange={(event) => update("montant", event.target.value)}
              placeholder="Ex. 25000"
              invalid={Boolean(errors.montant)}
            />
          </Field>

          <Field label="Date" htmlFor="dep-date" required error={errors.date} className="sm:col-span-2">
            <Input
              id="dep-date"
              type="date"
              value={form.date}
              onChange={(event) => update("date", event.target.value)}
              invalid={Boolean(errors.date)}
            />
          </Field>

          <Field label="Immeuble" htmlFor="dep-immeuble" hint="Rattachez un immeuble ou un bien.">
            <Select
              id="dep-immeuble"
              value={form.immeubleId}
              onChange={(event) => update("immeubleId", event.target.value)}
              options={immeubleOptions}
              placeholder={immeubles.length > 0 ? "Aucun immeuble en particulier" : "Aucun immeuble créé"}
              disabled={immeubles.length === 0}
            />
          </Field>

          <Field label="Bien" htmlFor="dep-bien" hint="Filtré selon l'immeuble.">
            <Select
              id="dep-bien"
              value={form.bienId}
              onChange={(event) => update("bienId", event.target.value)}
              options={bienOptions}
              placeholder={bienOptions.length > 0 ? "Aucun bien en particulier" : "Aucun bien disponible"}
              disabled={bienOptions.length === 0}
            />
          </Field>

          {errors.cible && (
            <p className="flex items-center gap-1.5 text-xs font-label text-red-600 sm:col-span-2" role="alert">
              <i className="ri-error-warning-line text-sm" aria-hidden="true" />
              {errors.cible}
            </p>
          )}

          <Field label="Description" htmlFor="dep-description" className="sm:col-span-2">
            <Textarea
              id="dep-description"
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
              placeholder="Détail de la dépense, prestataire, facture..."
              className="min-h-[96px]"
              maxLength={500}
            />
          </Field>

          <Field
            label="Justificatif"
            htmlFor="dep-justificatif"
            hint="PDF ou image (le fichier reste local)."
            className="sm:col-span-2"
          >
            <Input
              id="dep-justificatif"
              type="file"
              accept="application/pdf,image/*"
              onChange={handleJustificatif}
              className="file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-background-100 file:px-3 file:py-1.5 file:text-sm file:font-label file:text-foreground-800"
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}